import { Box, Divider, Drawer, Skeleton, Typography } from "@mui/material";
import { ReactComponent as Close } from "../../assets/icons/boxed-close.svg";
import { useSynopsis } from "./api/synopsisApi";

const fmtMoney = (v) =>
  `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtWeight = (v, unit) => `${Number(v).toFixed(3)} ${unit}`;

const CRM_WEB_URL = process.env.REACT_APP_CRM_WEB_URL || "https://crm.binnys.app";

const TILE_BG = "#0D0D0D";
const MUTED = "#8C8C8C";

const Label = ({ children, sx }) => (
  <Typography
    sx={{ fontFamily: "Karla", fontSize: ".75vw", fontWeight: 500, color: MUTED, ...sx }}
  >
    {children}
  </Typography>
);

const Value = ({ children, sx }) => (
  <Typography
    noWrap
    sx={{ fontFamily: "Poppins", fontSize: "1vw", fontWeight: 500, color: "#FCFAFA", ...sx }}
  >
    {children}
  </Typography>
);

const Tile = ({ children, sx, ...rest }) => (
  <Box
    {...rest}
    sx={{
      background: TILE_BG,
      borderRadius: ".25vw",
      padding: ".8vw",
      minWidth: 0,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const Section = ({ title, children, last }) => (
  <>
    <Box sx={{ padding: "1.2vw 1.2vw 0", display: "flex", flexDirection: "column", gap: ".8vw" }}>
      <Typography sx={{ fontFamily: "Karla", fontSize: ".9vw", fontWeight: 500, color: "#FFFFFF" }}>
        {title}
      </Typography>
      {children}
    </Box>
    {!last && (
      <Divider
        orientation="horizontal"
        flexItem
        style={{ border: ".15vw solid", borderColor: "#262626", width: "100%", margin: "1.2vw 0 0" }}
      />
    )}
  </>
);

const Shimmer = ({ width = "60%", height = "1vw" }) => (
  <Skeleton
    animation="wave"
    variant="rounded"
    width={width}
    height={height}
    sx={{ bgcolor: "#262626", borderRadius: ".25vw", maxWidth: "100%" }}
  />
);

const shown = (q, render, width) => {
  if (q.isLoading) return <Shimmer width={width} />;
  if (q.isError || !q.data) return "—";
  return render(q.data);
};

const Dues = ({ q }) => {
  const items = [
    { label: "Billed dues", value: (d) => fmtMoney(d.billedDues), color: "#FCFAFA" },
    { label: "Current dues", value: (d) => fmtMoney(d.currentDues), color: "#50C878" },
    { label: "Unfix gold", value: (d) => `${Number(d.unfixGold).toFixed(2)} g`, color: "#F5C242" },
  ];
  return (
    <Section title="Dues">
      <Box sx={{ display: "flex", gap: ".6vw" }}>
        {items.map((it) => (
          <Tile key={it.label} sx={{ flex: 1, display: "flex", flexDirection: "column", gap: ".35vw" }}>
            <Value sx={{ fontSize: ".8vw", color: it.color }}>{shown(q, it.value)}</Value>
            <Label>{it.label}</Label>
          </Tile>
        ))}
      </Box>
    </Section>
  );
};

const PipelineCard = ({ title, q, emptyText, href }) => {
  const d = q.data;
  const empty = d && d.bags === 0 && d.diamond === 0 && d.gold === 0;
  const rows = [
    ["Diamond weight", d && fmtWeight(d.diamond, "ct")],
    ["Gold weight", d && fmtWeight(d.gold, "g")],
  ];
  return (
    <Tile
      {...(href ? { component: "a", href, target: "_blank", rel: "noopener noreferrer" } : {})}
      sx={{
        textDecoration: "none",
        cursor: href ? "pointer" : "default",
        "&:hover": href ? { background: "#1A1A1A" } : undefined,
        display: "flex",
        flexDirection: "column",
        gap: ".5vw",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: ".5vw" }}>
        <Typography noWrap sx={{ fontFamily: "Karla", fontSize: ".9vw", fontWeight: 500, color: "#FFFFFF" }}>
          {title}
        </Typography>
        <Value>{shown(q, (x) => x.bags, "1.5vw")}</Value>
      </Box>
      {q.isLoading && (
        <>
          <Shimmer width="100%" height=".7vw" />
          <Shimmer width="100%" height=".7vw" />
        </>
      )}
      {d && empty && <Label sx={{ color: "#595959" }}>{emptyText}</Label>}
      {d && !empty &&
        rows.map(([label, val]) => (
          <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: ".5vw" }}>
            <Label>{label}</Label>
            <Typography noWrap sx={{ fontFamily: "Poppins", fontSize: ".75vw", color: "#FCFAFA" }}>
              {val}
            </Typography>
          </Box>
        ))}
    </Tile>
  );
};

const Ready = ({ q }) => {
  const items = [
    { label: "Articles ready", value: (d) => d.articles },
    { label: "Diamond weight", value: (d) => fmtWeight(d.diamond, "ct") },
    { label: "Gold weight", value: (d) => fmtWeight(d.gold, "g") },
  ];
  return (
    <Section title="Ready for billing" last>
      <Box sx={{ display: "flex", gap: ".6vw" }}>
        {items.map((it) => (
          <Tile key={it.label} sx={{ flex: 1, display: "flex", flexDirection: "column", gap: ".35vw" }}>
            <Value sx={{ fontSize: ".85vw" }}>{shown(q, it.value)}</Value>
            <Label>{it.label}</Label>
          </Tile>
        ))}
      </Box>
    </Section>
  );
};

const SynopsisDrawer = ({ open, onClose, clientCode, clientName }) => {
  const s = useSynopsis({ clientCode, clientName, enabled: open });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "30vw",
          background: "#141414",
          backgroundImage: "none",
          color: "#FFFFFF",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          paddingBottom: "1.2vw",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "1.2vw",
          gap: "1vw",
        }}
      >
        <Typography
          noWrap
          sx={{ fontFamily: "Karla", fontSize: ".9vw", fontWeight: 500, lineHeight: "1.1vw", minWidth: 0 }}
        >
          <span style={{ color: "#FFFFFF" }}>Synopsis</span>
          {clientName && <span style={{ color: "#595959" }}>{"  >  "}{clientName}</span>}
        </Typography>
        <Close style={{ cursor: "pointer", flexShrink: 0 }} onClick={onClose} />
      </Box>

      <Dues q={s.dues} />

      <Section title="Live pipeline">
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".6vw" }}>
          <PipelineCard title="Open quotes" q={s.quotes} emptyText="No Pending Quote" />
          <PipelineCard title="Open orders" q={s.orders} emptyText="No Pending Order" />
          <PipelineCard
            title="WIP"
            q={s.wip}
            emptyText={s.wip.data?.noRecord ? "No WIP record for this client" : "Nothing in production"}
            href={
              clientCode
                ? `${CRM_WEB_URL}/companies/${encodeURIComponent(clientCode)}/wip`
                : undefined
            }
          />
          <PipelineCard title="Memo" q={s.memo} emptyText="No Pending Memo" />
        </Box>
      </Section>

      <Ready q={s.ready} />
    </Drawer>
  );
};

export default SynopsisDrawer;
